import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { DatePicker } from "rsuite";
import PageTitle from "../../../layouts/PageTitle";
import { createDailyMaterialReportApi } from "../../../../services/dailyMaterialService";
import {
  useGetWorkProgressDropdownsQuery,
  useGetMorningMaterialsQuery,
} from "../../../../store/appApi";
import { setFormDirty } from "../../../../store/projectSlice";
import { selectHasModuleAccess } from "../../../../store/authSlice";
import "./style.css";
const EMPTY_MANUAL_ROW = {
  materialId: "",
  materialName: "",
  measurement: "",
  qtyIssued: "",
  qtyReturned: "",
  remarks: "",
  issuedToContractorId: "",
  balanceSnapshot: "",
  fromMorningReport: false,
};

const AddDailyMaterial = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasWrite = useSelector(
    selectHasModuleAccess("workProgress", "read-write"),
  );

  const [date, setDate] = useState("");
  // rows is empty until date is selected — then seeded from morning report
  const [rows, setRows] = useState([]);
  // per-material summary description + remarks (editable in summary table)
  const [summaryMeta, setSummaryMeta] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { data: dropdowns } = useGetWorkProgressDropdownsQuery();
  const materials = dropdowns?.materials ?? [];
  const contractors = dropdowns?.contractors ?? [];

  const {
    data: morningData,
    isFetching: morningLoading,
    isSuccess: morningFetched,
  } = useGetMorningMaterialsQuery(date, { skip: !date });

  // Seed locked rows from morning report when date changes
  useEffect(() => {
    if (!date || morningLoading || !morningFetched) return;

    const morningRows = (morningData?.materials ?? []).map((m) => ({
      materialId: String(m.materialId),
      materialName: m.materialName,
      measurement: m.measurement,
      qtyIssued: String(m.qtyIssued ?? 0),
      qtyReturned: "",
      description: "",
      remarks: "",
      issuedToContractorId: m.issuedToContractorId
        ? String(m.issuedToContractorId)
        : "",
      issuedToContractorName: m.issuedToContractorName ?? "",
      balanceSnapshot: String(m.balanceSnapshot ?? ""),
      fromMorningReport: true,
    }));

    setRows(morningRows);
    setSummaryMeta({});
  }, [date, morningLoading, morningFetched, morningData]);

  useEffect(() => {
    dispatch(setFormDirty(!!date));
    return () => dispatch(setFormDirty(false));
  }, [date, dispatch]);

  // Update a single editable cell in a row
  const updateRow = useCallback(
    (index, field, value) => {
      setRows((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;
          const updated = { ...row, [field]: value };
          // For manual rows, auto-fill name/unit on material select
          if (field === "materialId" && !row.fromMorningReport) {
            const mat = materials.find((m) => String(m._id) === value);
            if (mat) {
              updated.materialName = mat.skuName;
              updated.measurement = mat.measurement ?? "";
            }
          }
          return updated;
        }),
      );
    },
    [materials],
  );

  const addManualRow = useCallback(() => {
    setRows((prev) => [...prev, { ...EMPTY_MANUAL_ROW }]);
  }, []);

  const removeRow = useCallback((index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateSummaryMeta = (materialId, field, value) => {
    setSummaryMeta((prev) => ({
      ...prev,
      [materialId]: { ...(prev[materialId] ?? {}), [field]: value },
    }));
  };

  // Live summary — one row per unique materialId
  const summaryRows = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!r.materialId) return;
      const key = r.materialId;
      if (!map[key]) {
        const mat = materials.find((m) => String(m._id) === key);
        map[key] = {
          materialId: key,
          materialName: r.materialName || mat?.skuName || key,
          measurement: r.measurement || mat?.measurement || "",
          qtyIssued: 0,
          qtyReturned: 0,
        };
      }
      map[key].qtyIssued += Number(r.qtyIssued || 0);
      map[key].qtyReturned += Number(r.qtyReturned || 0);
    });
    return Object.values(map);
  }, [rows, materials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!date) errs.date = "Date is required.";

    const filledRows = rows.filter((r) => r.materialId);
    if (filledRows.length === 0)
      errs.rows = "At least one material entry is required.";

    filledRows.forEach((r, i) => {
      const ret = Number(r.qtyReturned || 0);
      const issued = Number(r.qtyIssued || 0);
      if (ret < 0) errs[`ret_${i}`] = "Cannot be negative.";
      if (issued > 0 && ret > issued)
        errs[`ret_${i}`] = "Cannot exceed qty issued.";
    });

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const summary = summaryRows.map((s) => ({
      materialId: s.materialId,
      materialName: s.materialName,
      measurement: s.measurement,
      qtyIssued: s.qtyIssued,
      qtyReturned: s.qtyReturned,
      remarks: summaryMeta[s.materialId]?.remarks ?? "",
    }));

    setSubmitting(true);
    try {
      await createDailyMaterialReportApi({
        date,
        morningReportId: morningData?.morningReportId ?? null,
        materials: filledRows,
        summary,
      });
      dispatch(setFormDirty(false));
      await Swal.fire({
        icon: "success",
        title: "Report saved!",
        timer: 1500,
        showConfirmButton: false,
      });
      navigate("/progress/daily-material/list");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save report.";
      const existingId = err?.response?.data?.existingId;
      if (existingId) {
        Swal.fire({
          icon: "warning",
          title: "Report already exists",
          text: msg,
          showCancelButton: true,
          confirmButtonText: "View existing report",
        }).then((r) => {
          if (r.isConfirmed)
            navigate(`/progress/daily-material/detail/${existingId}`);
        });
      } else {
        Swal.fire({ icon: "error", title: "Error", text: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasWrite) {
    return (
      <>
        <PageTitle activeMenu="Add Daily Report" motherMenu="Daily Progress" />
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
            <h5>Access Restricted</h5>
          </div>
        </div>
      </>
    );
  }

  const morningFound = morningFetched && !!morningData?.morningReportId;
  const morningMissing =
    morningFetched && !morningLoading && !morningData?.morningReportId;

  return (
    <>
      <PageTitle activeMenu="Add Daily Report" motherMenu="Daily Progress" />
      <form onSubmit={handleSubmit}>
        {/* ── Step 1: Pick date ───────────────────────────────── */}
        <div className="row mb-4">
          <div className="col-lg-5">
            <div className="card">
              <div className="card-body py-3">
                <label className="form-label fw-semibold mb-1">
                  Select Report Date <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center morning-report">
                  <div className="col-sm-4 mb-3">
                    <div className="mb-xl-0 mb-3">
                      <DatePicker
                        placeholder="Select Date"
                        format="dd/MM/yyyy"
                        placement="bottomStart"
                        className={errors.date ? "is-invalid" : ""}
                        value={date ? new Date(date) : null}
                        onChange={(value) => {
                          const formatted = value
                            ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
                            : "";
                          setDate(formatted);
                          setRows([]);
                          setSummaryMeta({});
                          setErrors((p) => ({ ...p, date: "" }));
                        }}
                      />
                    </div>
                  </div>
                  {/* <input
                    type="date"
                    className={`form-control ${errors.date ? "is-invalid" : ""}`}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setRows([]);
                      setSummaryMeta({});
                      setErrors((p) => ({ ...p, date: "" }));
                    }}
                  /> */}
                  {morningLoading && (
                    <span className="text-muted small d-flex align-items-center gap-1 text-nowrap">
                      <span className="spinner-border spinner-border-sm" />
                      Fetching morning report…
                    </span>
                  )}
                  {morningFound && (
                    <span
                      className="badge bg-success px-3 py-1 text-nowrap morning-repoert-span"
                      style={{ lineHeight: 1.5 }}
                    >
                      ✓ Morning report loaded
                    </span>
                  )}
                  {morningMissing && (
                    <span
                      className="badge bg-warning text-dark px-3 py-1 text-nowrap morning-repoert-span"
                      style={{ lineHeight: 1.5 }}
                    >
                      No morning report for this date
                    </span>
                  )}
                </div>
                {errors.date && (
                  <div className="text-danger small mt-1">{errors.date}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Step 2: Material table (only after date is picked) ─ */}
        {date && !morningLoading && (
          <>
            <div className="row mb-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <h5 className="card-title mb-0">Material Entries</h5>
                      <small className="text-muted">
                        {morningFound
                          ? "Materials from today's morning report — fill Qty Returned, Description and Remarks"
                          : "No morning report found — add entries manually below"}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addManualRow}
                    >
                      + Add More Items
                    </button>
                  </div>

                  {errors.rows && (
                    <div className="alert alert-danger mx-3 mt-3 mb-0">
                      {errors.rows}
                    </div>
                  )}

                  <div className="card-body p-0">
                    {rows.length === 0 ? (
                      /* No morning report + no manual rows yet */
                      <div className="text-center py-5 text-muted">
                        <p className="mb-2">
                          No morning report materials found for this date.
                        </p>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={addManualRow}
                        >
                          + Add Items Manually
                        </button>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table
                          className="table table-bordered align-middle mb-0"
                          style={{ minWidth: 860 }}
                        >
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 36 }}>#</th>
                              <th>Material</th>
                              <th>Issued To</th>
                              <th style={{ width: 70 }}>Unit</th>
                              <th style={{ width: 105 }}>Qty Issued</th>
                              <th style={{ width: 120 }}>Qty Returned</th>
                              {morningFound && (
                                <th style={{ width: 90 }}>Balance</th>
                              )}
                              <th>Remarks</th>
                              <th style={{ width: 36 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => (
                              <tr key={i}>
                                <td className="text-muted small">{i + 1}</td>

                                {/* Material */}
                                <td>
                                  {row.fromMorningReport ? (
                                    <span className="fw-semibold">
                                      {row.materialName || "—"}
                                    </span>
                                  ) : (
                                    <select
                                      className="form-select form-select-sm"
                                      value={row.materialId}
                                      onChange={(e) =>
                                        updateRow(
                                          i,
                                          "materialId",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <option value="">— Select —</option>
                                      {materials.map((m) => (
                                        <option key={m._id} value={m._id}>
                                          {m.skuName}
                                          {m.measurement
                                            ? ` (${m.measurement})`
                                            : ""}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>

                                {/* Issued To — locked for morning rows, dropdown for manual */}
                                <td>
                                  {row.fromMorningReport ? (
                                    <span className="text-muted small">
                                      {row.issuedToContractorName || "—"}
                                    </span>
                                  ) : (
                                    <select
                                      className="form-select form-select-sm"
                                      value={row.issuedToContractorId}
                                      onChange={(e) =>
                                        updateRow(
                                          i,
                                          "issuedToContractorId",
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <option value="">—</option>
                                      {contractors.map((c) => (
                                        <option key={c._id} value={c._id}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>

                                {/* Unit */}
                                <td className="text-muted small">
                                  {row.measurement || "—"}
                                </td>

                                {/* Qty Issued — locked for morning rows */}
                                <td>
                                  {row.fromMorningReport ? (
                                    <span className="badge badge-danger light px-3">
                                      {row.qtyIssued}
                                    </span>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      className="form-control form-control-sm"
                                      value={row.qtyIssued}
                                      onChange={(e) =>
                                        updateRow(
                                          i,
                                          "qtyIssued",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="0"
                                    />
                                  )}
                                </td>

                                {/* Qty Returned */}
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    className={`form-control form-control-sm ${errors[`ret_${i}`] ? "is-invalid" : ""}`}
                                    value={row.qtyReturned}
                                    onChange={(e) => {
                                      updateRow(
                                        i,
                                        "qtyReturned",
                                        e.target.value,
                                      );
                                      setErrors((p) => ({
                                        ...p,
                                        [`ret_${i}`]: "",
                                      }));
                                    }}
                                    placeholder="0"
                                  />
                                  {errors[`ret_${i}`] && (
                                    <div className="invalid-feedback">
                                      {errors[`ret_${i}`]}
                                    </div>
                                  )}
                                </td>

                                {/* Balance snapshot — only for morning rows */}
                                {morningFound && (
                                  <td className="text-muted small text-center">
                                    {row.fromMorningReport
                                      ? row.balanceSnapshot || "—"
                                      : "—"}
                                  </td>
                                )}

                                {/* Remarks */}
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={row.remarks}
                                    onChange={(e) =>
                                      updateRow(i, "remarks", e.target.value)
                                    }
                                    placeholder="Remarks"
                                  />
                                </td>

                                {/* Remove — only manual rows can be removed */}
                                <td className="text-center">
                                  {!row.fromMorningReport && (
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline-danger"
                                      onClick={() => removeRow(i)}
                                    >
                                      ×
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Summary ──────────────────────────────────────── */}
            {summaryRows.length > 0 && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Daily Summary</h5>
                      <small className="text-muted">
                        One row per material SKU · totals aggregated · add
                        Description &amp; Remarks
                      </small>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Material</th>
                              <th>Unit</th>
                              <th>Total Qty Issued</th>
                              <th>Total Qty Returned</th>
                              <th>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryRows.map((s, i) => (
                              <tr key={s.materialId}>
                                <td className="text-muted small">{i + 1}</td>
                                <td className="fw-semibold">
                                  {s.materialName}
                                </td>
                                <td className="text-muted small">
                                  {s.measurement || "—"}
                                </td>
                                <td>
                                  <span className="badge badge-danger light">
                                    {s.qtyIssued}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge badge-success light">
                                    {s.qtyReturned}
                                  </span>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Remarks"
                                    value={
                                      summaryMeta[s.materialId]?.remarks ?? ""
                                    }
                                    onChange={(e) =>
                                      updateSummaryMeta(
                                        s.materialId,
                                        "remarks",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Actions ──────────────────────────────────────── */}
            <div className="row">
              <div className="col-12 d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    "Save Report"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/progress/daily-material/list")}
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default AddDailyMaterial;
